import { T } from './i18n.ts';

/**
 * /paper 那一页（免费的模拟交易扩展：介绍、下载、怎么装）的三语文案。
 *
 * ── 这一页的立场 ──
 * 模拟交易是「拿假钱练手、复盘」，不是「先用假钱试试手气、再上真钱」。文案只说它能帮你练什么、
 * 它碰不到什么（钱包、真钱），不说「练好了去赚钱」。
 * 2026-10-30 起合进「交易风控卫士」扩展（老板：别让用户开开发者模式），这一页不再给单独的下载包。
 */
export const PX = {
  kicker: T('免费工具', '免費工具', 'Free tool'),
  h1: T('模拟交易练习', '模擬交易練習', 'Paper trading practice'),
  sub: T('在 GMGN 页面上用模拟资金练习买卖、加仓、减仓和复盘。不连钱包，不碰真钱，完全免费。',
    '在 GMGN 頁面上用模擬資金練習買賣、加倉、減倉和復盤。不連錢包，不碰真錢，完全免費。',
    'Practise buying, scaling in, trimming and reviewing trades on GMGN with simulated funds. No wallet, no real money, completely free.'),

  whatTitle: T('能做什么', '能做甚麼', 'What it does'),
  what: [
    T('在 GMGN 的战壕卡片和代币详情页上直接模拟买入、卖出、按比例卖、一键回本', '在 GMGN 的戰壕卡片和代幣詳情頁上直接模擬買入、賣出、按比例賣、一鍵回本',
      'Paper buy, sell, sell a percentage or recover your cost straight from GMGN trench cards and token pages'),
    T('六条链各自独立的模拟账户：Solana、BSC、Ethereum、Base、Robinhood Chain、X Layer', '六條鏈各自獨立的模擬帳戶：Solana、BSC、Ethereum、Base、Robinhood Chain、X Layer',
      'Six independent paper accounts: Solana, BSC, Ethereum, Base, Robinhood Chain, X Layer'),
    T('持仓、盈亏、买卖记录，K 线上标出你的模拟买卖点', '持倉、盈虧、買賣記錄，K 線上標出你的模擬買賣點',
      'Positions, PnL and fills, with your paper entries and exits marked on the chart'),
    T('盈亏日历、每日复盘笔记、日报图片，CSV / JSON 导出和备份', '盈虧日曆、每日復盤筆記、日報圖片，CSV / JSON 匯出和備份',
      'PnL calendar, daily review notes, daily report images, CSV / JSON export and backup')
  ],
  whyTitle: T('为什么放在交易风控卫士里', '為甚麼放在交易風控衛士裏', 'Why it is part of TradeGuard'),
  why: T('新的打法、新的仓位规则，先拿模拟资金跑一段、看清楚胜率和回撤，再决定要不要用真钱。纪律是练出来的，不是靠运气试出来的。',
    '新的打法、新的倉位規則，先拿模擬資金跑一段、看清楚勝率和回撤，再決定要不要用真錢。紀律是練出來的，不是靠運氣試出來的。',
    'Run a new approach or position rule with simulated funds first, see its real win rate and drawdown, then decide whether it deserves real money. Discipline is practised, not stumbled into.'),

  safeTitle: T('它碰不到你的钱', '它碰不到你的錢', 'It cannot touch your money'),
  safe: [
    T('不连钱包，不要助记词、私钥，不签名、不授权、不广播任何链上交易', '不連錢包，不要助記詞、私鑰，不簽名、不授權、不廣播任何鏈上交易',
      'No wallet connection, no seed phrase or private key, no signatures, approvals or on-chain transactions'),
    T('它会挡住 GMGN 原来的「快速买入」按钮，换成模拟的那一个，免得误点成真买', '它會擋住 GMGN 原來的「快速買入」按鈕，換成模擬的那一個，免得誤點成真買',
      'It covers GMGN’s own quick-buy buttons with paper ones, so a misclick cannot become a real buy'),
    T('数据只存在你这台电脑的浏览器里，不发到我们的服务器', '數據只存在你這台電腦的瀏覽器裏，不發到我們的伺服器',
      'Your data stays in this browser on this computer; nothing is sent to our servers'),
    T('只请求公开行情：GMGN 页面本身、CoinGecko 和 DEX Screener 的公开价格', '只請求公開行情：GMGN 頁面本身、CoinGecko 和 DEX Screener 的公開價格',
      'It only fetches public market data: the GMGN page itself, plus public prices from CoinGecko and DEX Screener')
  ],

  installTitle: T('怎么用', '怎麼用', 'How to use it'),
  installNote: T('模拟交易已经合进「交易风控卫士」浏览器扩展（2026-10-30），不用再单独装、也不用开开发者模式。扩展正在提交 Chrome 应用商店，上架之后一键安装。',
    '模擬交易已經合進「交易風控衛士」瀏覽器擴充功能（2026-10-30），不用再單獨裝、也不用開開發者模式。擴充功能正在提交 Chrome 應用商店，上架之後一鍵安裝。',
    'Paper trading is now part of the TradeGuard browser extension (2026-10-30): no separate install and no developer mode. The extension is being submitted to the Chrome Web Store; once listed it installs in one click.'),
  /* 扩展上架之后（运维后台填了商店链接，lib/ext-store.ts）换成这一句。上面那句「正在提交」在上架之后就是一句错话 */
  installNoteListed: T('模拟交易已经合进「交易风控卫士」浏览器扩展，不用单独装、也不用开开发者模式。扩展已经在 Chrome 应用商店上架，点上面的按钮去商店，点「添加至 Chrome」就装好了。',
    '模擬交易已經合進「交易風控衛士」瀏覽器擴充功能，不用單獨裝、也不用開開發者模式。擴充功能已經在 Chrome 應用商店上架，按上面的按鈕去商店，按「加到 Chrome」就裝好了。',
    'Paper trading is part of the TradeGuard browser extension: no separate install and no developer mode. The extension is in the Chrome Web Store; use the button above and click "Add to Chrome".'),
  getExtStore: T('去 Chrome 应用商店安装', '去 Chrome 應用商店安裝', 'Install from the Chrome Web Store'),
  steps: [
    T('装好交易风控卫士扩展', '裝好交易風控衛士擴充功能', 'Install the TradeGuard extension'),
    T('打开 GMGN（上面的「打开 GMGN」），按提示设一个模拟本金，就可以开始练了', '打開 GMGN（上面的「打開 GMGN」），按提示設一個模擬本金，就可以開始練了', 'Open GMGN (the "Open GMGN" button above), set a paper balance when asked, and start practising'),
    T('看持仓、盈亏日历和复盘：点扩展图标，再点「模拟交易」', '看持倉、盈虧日曆和復盤：點擴充功能圖示，再點「模擬交易」', 'For positions, the PnL calendar and reviews: click the extension icon, then "Paper trading"')
  ],
  updateNote: T('以前单独装过模拟交易扩展的：新扩展装好之后把旧的删掉就行。旧扩展里的模拟账户不会自动搬过来，要的话先在旧扩展里导出 JSON，再到新扩展里导入。',
    '以前單獨裝過模擬交易擴充功能的：新擴充功能裝好之後把舊的刪掉就行。舊擴充功能裏的模擬帳戶不會自動搬過來，要的話先在舊擴充功能裏匯出 JSON，再到新擴充功能裏匯入。',
    'If you installed the old standalone paper-trading extension: remove it once the new one is installed. Paper accounts do not move over automatically; export JSON from the old one and import it into the new one if you want them.'),

  creditTitle: T('关于', '關於', 'About'),
  credit: T('基于开源项目 Tango Paper Trader（作者 @oooooyoung）修改，经作者同意使用。界面、提示音和配色换成了交易风控卫士的，原作者的邀请码和推广链接都去掉了。',
    '基於開源項目 Tango Paper Trader（作者 @oooooyoung）修改，經作者同意使用。介面、提示音和配色換成了交易風控衛士的，原作者的邀請碼和推廣連結都去掉了。',
    'Based on the open-source Tango Paper Trader by @oooooyoung, used with the author’s permission. The interface, sounds and colours were changed to TradeGuard’s, and the original author’s referral code and promotional links were removed.'),
  referral: T('扩展里的「打开 GMGN」按钮带交易风控卫士的 GMGN 邀请码。它不改变模拟交易的任何费用，也不是使用这个免费工具的条件；扩展不会在你自己打开的 GMGN 页面上添加或替换邀请码。',
    '擴充功能裏的「打開 GMGN」按鈕帶交易風控衛士的 GMGN 邀請碼。它不改變模擬交易的任何費用，也不是使用這個免費工具的條件；擴充功能不會在你自己打開的 GMGN 頁面上添加或替換邀請碼。',
    'The extension’s "Open GMGN" button carries TradeGuard’s GMGN referral code. It does not change any paper-trading fee and is not a condition for using this free tool; the extension never adds or replaces referral codes on GMGN pages you open yourself.'),
  getExt: T('它在交易风控卫士扩展里', '它在交易風控衛士擴充功能裏', 'It is part of the TradeGuard extension'),
  openGmgn: T('打开 GMGN', '打開 GMGN', 'Open GMGN'),
  invite: T('「打开 GMGN」是交易风控卫士的邀请链接：用它注册，平台会给我们返佣，不影响你的任何费用。', '「打開 GMGN」是交易風控衛士的邀請連結：用它註冊，平台會給我們返佣，不影響你的任何費用。',
    '"Open GMGN" is a TradeGuard referral link: sign up through it and the platform pays us a commission; your fees do not change.'),
  nav: T('模拟交易', '模擬交易', 'Paper trading')
} as const;
